/** Role-shell path helpers — keep nested pages inside Learner / Studio / Org / Platform. */

export function detectRoleShell(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    if (pathname.startsWith('/learn')) return 'learn';
    if (pathname.startsWith('/studio')) return 'studio';
    if (pathname.startsWith('/org/admin')) return 'org';
    if (pathname.startsWith('/super-admin')) return 'platform';
    return null;
}

/** True when page is rendered inside a role shell (skip marketing Header). */
export function isInsideRoleShell(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    return Boolean(detectRoleShell(pathname));
}

/**
 * Full-bleed workspace routes (course player, GenUI builder, project canvas, exams).
 * These need edge-to-edge width inside the shell.
 */
export function isImmersiveShellPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    if (!pathname) return false;
    if (pathname === '/learn/course' || pathname.startsWith('/learn/course/')) return true;
    if (pathname === '/learn/exam') return true;
    if (pathname === '/learn/genui' || pathname.startsWith('/learn/genui/')) return true;
    if (pathname === '/learn/create-choice' || pathname.startsWith('/learn/create-choice/')) return true;
    if (pathname === '/studio/genui' || pathname.startsWith('/studio/genui/')) return true;
    if (pathname === '/studio/create-choice' || pathname.startsWith('/studio/create-choice/')) return true;
    if (pathname === '/studio/exam' || pathname === '/studio/review') return true;
    if (/^\/studio\/projects\/[^/]+/.test(pathname)) return true;
    if (/^\/learn\/projects\/[^/]+/.test(pathname)) return true;
    return false;
}

/** Routes that must fill the viewport (internal scroll only) — course player & GenUI builder. */
export function isViewportFillShellPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    if (!pathname) return false;
    if (pathname === '/learn/course' || pathname.startsWith('/learn/course/')) return true;
    if (pathname === '/learn/genui' || pathname.startsWith('/learn/genui/')) return true;
    if (pathname === '/learn/create-choice' || pathname.startsWith('/learn/create-choice/')) return true;
    if (pathname === '/studio/genui' || pathname.startsWith('/studio/genui/')) return true;
    if (pathname === '/studio/create-choice' || pathname.startsWith('/studio/create-choice/')) return true;
    return false;
}

/**
 * Immersive learning / builder surfaces where the nav rail should auto-collapse
 * (icons-only) so content gets focus. Includes immersive routes plus practice.
 */
export function isFocusCollapseShellPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    if (!pathname) return false;
    if (isImmersiveShellPath(pathname)) return true;
    if (pathname === '/learn/practice' || pathname.startsWith('/learn/practice/')) return true;
    if (pathname === '/learn/microlearning' || pathname.startsWith('/learn/microlearning/')) return true;
    return false;
}

/**
 * Force icons-only rail only when *entering* focus mode.
 * Keep the user's expand choice while navigating between focus routes
 * (or on remounts that do not leave focus).
 */
export function shouldForceCollapseRail(prevFocusMode, nextFocusMode) {
    return Boolean(nextFocusMode) && !Boolean(prevFocusMode);
}

const LEARN_MAP = {
    '/home': '/learn',
    '/course': '/learn/course',
    '/my-classes': '/learn/classes',
    '/my-certificates': '/learn/certificates',
    '/certificates': '/learn/certificates',
    '/profile': '/learn/profile',
    '/exam': '/learn/exam',
    '/microlearning': '/learn/practice',
    '/practice': '/learn/practice',
    '/analytics': '/learn/analytics',
    '/create-choice': '/learn/create-choice',
    '/create': '/learn/create-choice',
    '/genui': '/learn/genui',
    '/projects': '/learn/projects',
    '/subscription': '/learn/subscription',
    '/manage': '/learn/subscription',
    '/course-ideas': '/studio/ideas',
};

const STUDIO_MAP = {
    '/home': '/studio',
    '/projects': '/studio/projects',
    '/genui': '/studio/genui',
    '/my-classes': '/studio/classes',
    '/course-ideas': '/studio/ideas',
    '/topics': '/studio/ideas',
    '/course-review': '/studio/review',
    '/create-choice': '/studio/create-choice',
    '/create': '/studio/create-choice',
    '/exam': '/studio/exam',
    '/course': '/learn/course',
    '/subscription': '/learn/subscription',
    '/manage': '/learn/subscription',
    '/profile': '/learn/profile',
};

/**
 * Map a legacy app path to the current shell equivalent.
 * @param {string} path e.g. '/projects' or '/projects/abc'
 * @param {string} [pathname] current location
 */
export function shellAwarePath(path, pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    if (!path || typeof path !== 'string') return path;
    const shell = detectRoleShell(pathname);
    if (!shell) return path;

    const hashIdx = path.indexOf('#');
    const hash = hashIdx >= 0 ? path.slice(hashIdx) : '';
    const withoutHash = hashIdx >= 0 ? path.slice(0, hashIdx) : path;
    const qIdx = withoutHash.indexOf('?');
    const query = qIdx >= 0 ? withoutHash.slice(qIdx) : '';
    const base = qIdx >= 0 ? withoutHash.slice(0, qIdx) : withoutHash;

    if (shell === 'studio' && base.startsWith('/projects/')) {
        return `/studio${base}${query}${hash}`;
    }
    if (shell === 'learn' && base.startsWith('/projects/')) {
        return `/learn${base}${query}${hash}`;
    }
    if (shell === 'learn' || shell === 'org' || shell === 'platform') {
        const mapped = LEARN_MAP[base];
        if (mapped) return `${mapped}${query}${hash}`;
    }
    if (shell === 'studio') {
        const mapped = STUDIO_MAP[base];
        if (mapped) return `${mapped}${query}${hash}`;
    }
    return path;
}

/** Preferred course player path for the active shell. */
export function shellCoursePath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
    return shellAwarePath('/course', pathname);
}
